import {
  ClipboardList,
  FileText,
  Globe,
  LineChart,
  Linkedin,
  ScrollText,
  Search,
  ShieldAlert,
  Table2,
  Target,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { aiApi } from "@/api/ai-api";
import type { DealTransformed } from "@/lib/schema/deal-schema";

export type ReportSurface = "report" | "analysis";

export interface ReportItemContext {
  currentDeal: DealTransformed;
  workspaceId: string;
}

export interface ReportItemConfig {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  surface: ReportSurface;
  isPremium: boolean;
  /** Key inside `currentDeal.aiGenerations` where the versions array lives. */
  dataKey: string;
  /** Report_status key when it differs from dataKey. */
  statusKey?: string;
  /** Whether the report view gates behind `currentDeal.aiTracked`. */
  requiresActivation: boolean;
  /** Hide the version dropdown for this type (valuations / deal-memo). */
  skipVersions?: boolean;
  /** Hide the Generate New button for this type (static deal-memo). */
  skipGenerate?: boolean;
  /** Hide the Delete button for this type (valuations handles its own, deal-memo is static). */
  skipDelete?: boolean;
  /**
   * Some reports' array entries are "present but empty" and shouldn't count as
   * available versions. Mirrors the metrics behaviour in reports-tab.tsx.
   */
  isReportValid?: (entry: unknown) => boolean;
  /** Kick off generation. Parent handles `activateDeal` before calling this. */
  triggerGenerate: (ctx: ReportItemContext) => Promise<unknown>;
  /** Delete a specific version. */
  deleteVersion: (ctx: ReportItemContext, versionId: string) => Promise<unknown>;
}

const makeStandardParams = ({ currentDeal, workspaceId }: ReportItemContext) => ({
  companyId: currentDeal.id,
  opportunityId: workspaceId,
  applicationInfo: currentDeal.dealInfo.applicationInfo,
  applicationObject: currentDeal.dealInfo.applicationInfo,
});

const defaultDelete = async (_: ReportItemContext, versionId: string) =>
  aiApi.deleteAiGeneration({ generationId: versionId });

export const REPORT_ITEMS: ReportItemConfig[] = [
  // ---------- Reports surface ----------
  {
    id: "tear-sheet",
    title: "Tear Sheet",
    description: "1-2 page executive summary snapshot",
    icon: FileText,
    surface: "report",
    isPremium: false,
    // Panel toolbar reads from the unified `tearsheets_all` virtual
    // key so legacy and new-shape tearsheets both show up in the
    // version dropdown.
    dataKey: "tearsheets_all",
    statusKey: "tearsheet",
    requiresActivation: false,
    isReportValid: (entry: unknown): boolean => {
      if (!entry || typeof entry !== "object") return false;
      const e = entry as { kind?: unknown };
      return e.kind === "legacy" || e.kind === "v2";
    },
    triggerGenerate: (ctx) =>
      aiApi.startCustomizeUiTearsheet({
        companyId: ctx.currentDeal.id,
        opportunityId: ctx.workspaceId,
      }),
    deleteVersion: defaultDelete,
  },
  {
    id: "deal-brief",
    title: "Deal Brief",
    description: "Deep diligence evaluation report",
    icon: ScrollText,
    surface: "report",
    isPremium: true,
    dataKey: "summaries",
    statusKey: "summary",
    requiresActivation: true,
    triggerGenerate: (ctx) => aiApi.getDealBrief(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "metrics",
    title: "Metrics",
    description: "Company financial metrics and KPIs",
    icon: Table2,
    surface: "report",
    isPremium: true,
    dataKey: "metrics",
    requiresActivation: true,
    isReportValid: (entry) => {
      const r = entry as { metrics?: Array<{ data?: unknown[] }> };
      return (
        Array.isArray(r?.metrics) &&
        r.metrics.length > 0 &&
        r.metrics.some((m) => Array.isArray(m?.data))
      );
    },
    triggerGenerate: (ctx) => aiApi.getMetrics(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "deal-memo",
    title: "Deal Memo",
    description: "Investment memorandum",
    icon: FileText,
    surface: "report",
    isPremium: true,
    dataKey: "__deal_memo_static__",
    requiresActivation: false,
    skipVersions: true,
    skipGenerate: true,
    skipDelete: true,
    triggerGenerate: async () => undefined,
    deleteVersion: async () => undefined,
  },

  // ---------- Analysis surface ----------
  {
    id: "deal-fit",
    title: "Deal Fit",
    description: "Evaluates alignment with fund thesis and investment criteria",
    icon: Target,
    surface: "analysis",
    isPremium: false,
    dataKey: "deal_fit",
    requiresActivation: false,
    triggerGenerate: (ctx) => aiApi.getDealFit(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "gap-analysis",
    title: "Gap Analysis",
    description: "Identifies missing information and areas requiring further diligence",
    icon: Search,
    surface: "analysis",
    isPremium: false,
    dataKey: "gap_analysis",
    requiresActivation: true,
    triggerGenerate: (ctx) => aiApi.getGapAnalysis(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "insights",
    title: "Insights",
    description: "AI-generated business insights and findings",
    icon: LineChart,
    surface: "analysis",
    isPremium: true,
    dataKey: "insights",
    requiresActivation: true,
    triggerGenerate: (ctx) => aiApi.getInsights(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "market-intel",
    title: "Market Intelligence",
    description: "Market research, competitive landscape, and industry trends",
    icon: TrendingUp,
    surface: "analysis",
    isPremium: true,
    dataKey: "market_research",
    requiresActivation: false,
    triggerGenerate: (ctx) =>
      aiApi.getMarketIntelligence(makeStandardParams(ctx)),
    deleteVersion: (ctx, versionId) =>
      aiApi.deleteMarketResearch({
        reportId: versionId,
        companyId: ctx.currentDeal.id,
        workspaceId: ctx.workspaceId,
      }),
  },
  {
    id: "business-risk",
    title: "Business Risk",
    description: "Risk assessment and scoring",
    icon: ShieldAlert,
    surface: "analysis",
    isPremium: true,
    dataKey: "startupscores",
    statusKey: "startupscore",
    requiresActivation: true,
    triggerGenerate: (ctx) => aiApi.getStartupScore(makeStandardParams(ctx)),
    deleteVersion: defaultDelete,
  },
  {
    id: "valuations",
    title: "Valuations",
    description: "Valuation analysis and benchmarking",
    icon: TrendingUp,
    surface: "analysis",
    isPremium: true,
    dataKey: "valuations",
    requiresActivation: true,
    // Valuations is end-to-end owned by `useValuations` (synchronous BE
    // endpoint + its own local state for the versions list). Routing it
    // through the shared toolbar would duplicate state. Toolbar shows only
    // Close for this type; the inline Valuations UI keeps its own controls.
    skipVersions: true,
    skipGenerate: true,
    skipDelete: true,
    triggerGenerate: (ctx) =>
      aiApi.generateValuationsReport(ctx.workspaceId, ctx.currentDeal.id),
    deleteVersion: async (ctx, versionId) =>
      aiApi.deleteValuationsReport(versionId, ctx.currentDeal.id, ctx.workspaceId),
  },

  // ---------- Source-data reports (legacy per-deal sidebar items) ----------
  // Wired through the same panel toolbar pattern as the AI reports
  // above so the chrome stays consistent (version dropdown, generate,
  // delete, close). Questionnaire is the odd one out — there's no
  // versioning model on the BE, just a single set of question/answers,
  // so version + delete are hidden but Generate (regenerate all
  // answers) goes through the toolbar.
  {
    id: "website-info",
    title: "Website Info",
    description: "Scraped website content, summaries, and metadata",
    icon: Globe,
    surface: "analysis",
    isPremium: false,
    dataKey: "website_summary_v2",
    statusKey: "website_summary_v2",
    requiresActivation: false,
    triggerGenerate: (ctx) => {
      const websiteUrl = (ctx.currentDeal?.dealInfo.deal?.find(
        (f) => f.field_name === "company_website",
      )?.field_value || ctx.currentDeal?.overview.website) as string;
      if (!websiteUrl) {
        return Promise.reject(
          new Error("Add a website URL to the deal before analysing"),
        );
      }
      return aiApi.summarizeWebsite({
        companyId: ctx.currentDeal.id,
        opportunityId: ctx.workspaceId,
        url: websiteUrl,
      });
    },
    deleteVersion: (ctx, versionId) =>
      aiApi.deleteWebsiteSummaryV2({
        reportId: versionId,
        companyId: ctx.currentDeal.id,
        workspaceId: ctx.workspaceId,
      }),
  },
  {
    id: "linkedin-info",
    title: "LinkedIn Info",
    description: "LinkedIn-scraped company profile and team data",
    icon: Linkedin,
    surface: "analysis",
    isPremium: false,
    dataKey: "linkedInInfo",
    statusKey: "linkedin_company_scrape",
    requiresActivation: false,
    triggerGenerate: async (ctx) => {
      const linkedInField = ctx.currentDeal?.dealInfo.deal?.find(
        (f) => f.field_name === "linkedIn",
      );
      const linkedInUrl =
        linkedInField?.field_state !== "pending"
          ? ((linkedInField?.field_value || "") as string)
          : "";
      if (!linkedInUrl) {
        return Promise.reject(
          new Error("Add a LinkedIn URL to the deal before scraping"),
        );
      }
      const { linkedInScraperApi } = await import(
        "@/api/submission-api"
      );
      return linkedInScraperApi.scrapeLinkedIn(
        linkedInUrl,
        ctx.currentDeal.id,
        ctx.workspaceId,
      );
    },
    deleteVersion: (ctx, versionId) =>
      aiApi.deleteLinkedInInfo({
        reportId: versionId,
        companyId: ctx.currentDeal.id,
        workspaceId: ctx.workspaceId,
      }),
  },
  {
    id: "questionnaire",
    title: "Questionnaire",
    description: "Founder-submitted application questionnaire responses",
    icon: ClipboardList,
    surface: "analysis",
    isPremium: false,
    // Questionnaire is a static info view — the questions belong to
    // the workspace and per-question regeneration happens inline on
    // each row inside the panel. Versions + delete + the generic
    // "New version" button stay hidden; when questions exist the panel
    // registers its own "Generate all answers" toolbar action instead
    // (empty configs show an inline "Add questions" CTA).
    dataKey: "__questionnaire__",
    requiresActivation: false,
    skipVersions: true,
    skipGenerate: true,
    skipDelete: true,
    triggerGenerate: async () => undefined,
    deleteVersion: async () => undefined,
  },
];

export const REPORT_ITEMS_BY_ID: Record<string, ReportItemConfig> =
  Object.fromEntries(REPORT_ITEMS.map((item) => [item.id, item]));

export const getReportItem = (id: string): ReportItemConfig | undefined =>
  REPORT_ITEMS_BY_ID[id];

export const reportsForSurface = (surface: ReportSurface): ReportItemConfig[] =>
  REPORT_ITEMS.filter((item) => item.surface === surface);
